import React from "react";
import { useBloodDemandPredictions, useAreaPredictions, usePredictionSummary } from "../../../hooks/useDemandAnalytics";
import StatCard from "../../../components/admin/StatCard";

const DEMAND_COLORS = {
  LOW: "badge-success",
  MEDIUM: "badge-info",
  HIGH: "badge-warning",
  CRITICAL: "badge-error",
};

const TREND_ICONS = {
  up: { icon: "fa-arrow-trend-up", color: "text-red-500" },
  down: { icon: "fa-arrow-trend-down", color: "text-green-500" },
  stable: { icon: "fa-minus", color: "text-gray-400" },
};

const PredictionsTab = () => {
  const { data: bloodPred, isLoading: bloodLoading } = useBloodDemandPredictions();
  const { data: areaPred, isLoading: areaLoading } = useAreaPredictions();
  const { data: summary, isLoading: summaryLoading } = usePredictionSummary();

  const isLoading = bloodLoading || areaLoading || summaryLoading;

  if (isLoading) {
    return <div className="skeleton h-64 rounded-lg"></div>;
  }

  const bloodItems = bloodPred?.data || [];
  const areaItems = areaPred?.data || [];
  const summaryData = summary?.data || {};
  const aiInsights = summary?.aiInsights;

  const hasEnoughData = summaryData.hasEnoughData !== false;

  if (!hasEnoughData) {
    return (
      <div className="space-y-4">
        <div className="alert alert-info">
          <i className="fa-solid fa-circle-info"></i>
          <div>
            <h3 className="font-bold">Insufficient historical data</h3>
            <p className="text-sm">
              More historical data is required for reliable prediction. The system
              needs at least 2 weeks of blood request data to generate meaningful
              predictions. Predictions will improve as more data accumulates.
            </p>
          </div>
        </div>
        {bloodItems.length > 0 && (
          <div className="card bg-base-100 shadow">
            <div className="card-body">
              <h2 className="card-title text-sm">Preliminary Blood Group Estimates</h2>
              <div className="overflow-x-auto">
                <table className="table table-zebra table-sm">
                  <thead>
                    <tr>
                      <th>Blood Group</th>
                      <th>Predicted</th>
                      <th>Level</th>
                      <th>Confidence</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bloodItems.map((p) => {
                      const trend = TREND_ICONS[p.trend] || TREND_ICONS.stable;
                      return (
                        <tr key={p.group}>
                          <td>
                            <span className="badge badge-sm badge-error">{p.group}</span>
                          </td>
                          <td className="text-sm">{p.predictedDemand} requests</td>
                          <td>
                            <span className={`badge badge-sm ${DEMAND_COLORS[p.demandLevel] || "badge-ghost"}`}>
                              {p.demandLevel}
                            </span>
                          </td>
                          <td className="text-sm">{p.confidence}%</td>
                          <td className={`text-sm ${trend.color}`}>
                            <i className={`fa-solid ${trend.icon}`}></i>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Overall Demand"
          value={summaryData.overallDemandLevel || "N/A"}
          icon="fa-gauge-high"
          color="bg-red-50"
          textColor="text-red-600"
        />
        <StatCard
          title="Critical Groups"
          value={summaryData.criticalGroups?.length || 0}
          icon="fa-triangle-exclamation"
          color="bg-orange-50"
          textColor="text-orange-600"
        />
        <StatCard
          title="Top Requested Group"
          value={summaryData.topRequestedGroup || "N/A"}
          icon="fa-droplet"
          color="bg-blue-50"
          textColor="text-blue-600"
        />
        <StatCard
          title="Top Demand Area"
          value={summaryData.topDemandArea || "N/A"}
          icon="fa-map-marker"
          color="bg-green-50"
          textColor="text-green-600"
        />
      </div>

      {aiInsights && (
        <div className="card bg-gradient-to-r from-blue-50 to-purple-50 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">
              <i className="fa-solid fa-robot text-blue-600"></i> AI Insights
            </h2>
            {aiInsights.insights && (
              <ul className="list-disc list-inside space-y-1 text-sm">
                {aiInsights.insights.map((insight, i) => (
                  <li key={i}>{insight}</li>
                ))}
              </ul>
            )}
            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className="mt-2">
                <span className="text-xs font-semibold text-blue-700">Recommendations:</span>
                <ul className="list-disc list-inside space-y-1 text-sm mt-1">
                  {aiInsights.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Blood Group Demand Predictions (Next 4 Weeks)</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th>Blood Group</th>
                  <th>Predicted Demand</th>
                  <th>Level</th>
                  <th>Confidence</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {bloodItems.map((p) => {
                  const trend = TREND_ICONS[p.trend] || TREND_ICONS.stable;
                  return (
                    <tr key={p.group}>
                      <td>
                        <span className="badge badge-sm badge-error">{p.group}</span>
                      </td>
                      <td className="text-sm">{p.predictedDemand} requests</td>
                      <td>
                        <span className={`badge badge-sm ${DEMAND_COLORS[p.demandLevel] || "badge-ghost"}`}>
                          {p.demandLevel}
                        </span>
                      </td>
                      <td className="text-sm">{p.confidence}%</td>
                      <td className={`text-sm ${trend.color}`}>
                        <i className={`fa-solid ${trend.icon}`}></i>
                      </td>
                    </tr>
                  );
                })}
                {bloodItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-400">
                      No prediction data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Area Demand Predictions (Next 4 Weeks)</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra table-sm">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Predicted Demand</th>
                  <th>Level</th>
                  <th>Confidence</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {areaItems.map((p, i) => {
                  const trend = TREND_ICONS[p.trend] || TREND_ICONS.stable;
                  return (
                    <tr key={i}>
                      <td className="text-sm font-medium">{p.district}</td>
                      <td className="text-sm">{p.predictedDemand} requests</td>
                      <td>
                        <span className={`badge badge-sm ${DEMAND_COLORS[p.demandLevel] || "badge-ghost"}`}>
                          {p.demandLevel}
                        </span>
                      </td>
                      <td className="text-sm">{p.confidence}%</td>
                      <td className={`text-sm ${trend.color}`}>
                        <i className={`fa-solid ${trend.icon}`}></i>
                      </td>
                    </tr>
                  );
                })}
                {areaItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-400">
                      No prediction data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {bloodPred?.explanation && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">
              <i className="fa-solid fa-robot"></i> AI Prediction Explanation
            </h2>
            <p className="text-sm text-gray-600">{bloodPred.explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictionsTab;
